import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Button from '../../../shared/components/ui/Button';
import FormInput from '../../../shared/components/ui/FormInput';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { createClosedDeal, getUnitReservationParties } from '../../services/api/implementation/impl';
import toast from 'react-hot-toast';
import Modal from '../../../shared/components/ui/Modal';
import { SelectLead } from './SelectLead';
import { CloseDeal } from './CloseDeal';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';

// Reserved statuses name who the unit is kept for.
const RESERVED_PARTY_LABELS = {
  Reserved_for_Builder: 'Builder / shareholder',
  Reserved_for_Land_Owner: 'Land owner',
  Reserved_for_Company: 'Company'
};
const ADD_NEW = '__add_new__';

// Validation schema
const validationSchema = yup.object().shape({
  status: yup.string().required('Status is required')
});

export const UpdateStatus = ({
  onClose,
  onSubmit,
  statuses,
  type,
  isSubmitting,
  unitData,
  projectName,
  projectId
}) => {
  const navigate = useNavigate();
  const userId = Cookies.get('userId');
  const [isSelectLeadModalOpen, setIsSelectLeadModalOpen] = useState(false);
  const [isCloseDealModalOpen, setIsCloseDealModalOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      status: ''
    }
  });

  const selectedStatus = watch('status');
  const reservedLabel = type === 'unitPricing' ? RESERVED_PARTY_LABELS[selectedStatus] : null;
  const [partyRef, setPartyRef] = useState('');
  const [newPartyName, setNewPartyName] = useState('');
  const [partyError, setPartyError] = useState('');

  const { data: partyData, isFetching: isLoadingParties } = useQuery({
    queryKey: ['unitReservationParties', unitData?.id, selectedStatus],
    queryFn: () => getUnitReservationParties(unitData.id, selectedStatus),
    enabled: Boolean(reservedLabel && unitData?.id)
  });
  const partyOptions = partyData?.options ?? [];

  useEffect(() => {
    setPartyRef('');
    setNewPartyName('');
    setPartyError('');
  }, [selectedStatus]);

  // Watch for status changes and open lead selection modal if "Booked" is selected

  // Track page view
  useEffect(() => {
    mixpanel.track('Update Status Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    if (selectedStatus === 'Booked') {
      setIsSelectLeadModalOpen(true);
    }
  }, [selectedStatus]);

  // Mutation for creating closed deal
  const { mutate: createClosedDealMutation } = useMutation({
    mutationFn: (data) => createClosedDeal(data),
    onSuccess: () => {
      toast.success('Deal closed successfully');
      setIsCloseDealModalOpen(false);
      // Update the unit status to Booked
      onSubmit({ status: 'Booked' });
      onClose();
      if (selectedLead?.id) {
        navigate(`/builder/deals/${selectedLead.id}`, { state: { status: 'Closed Won' } });
      }
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to close deal');
    }
  });

  const onSubmitForm = (data) => {
    if (reservedLabel) {
      let reserved_party = null;
      if (partyRef === ADD_NEW) {
        const name = newPartyName.trim();
        if (!name) {
          setPartyError(`Type the ${reservedLabel.toLowerCase()} name`);
          return;
        }
        reserved_party = { name };
      } else if (partyRef) {
        reserved_party = { ref: partyRef };
      } else {
        setPartyError(`Choose the ${reservedLabel.toLowerCase()} this unit is reserved for`);
        return;
      }
      onSubmit({ ...data, reserved_party });
      onClose();
      reset();
      return;
    }
    if (data.status !== 'Booked') {
      onSubmit(data);
      onClose();
      reset();
    }
  };

  const handleLeadSelect = (lead) => {
    setSelectedLead(lead);
    setIsSelectLeadModalOpen(false);
    setIsCloseDealModalOpen(true);
  };

  const handleCloseDeal = (data) => {
    createClosedDealMutation(data);
  };

  console.log({ statuses });

  return (
    <>
      {/* <div className="flex flex-col justify-between items-start gap-4">
        <span className="text-lg border-b-[1px] border-stone-200 w-full pb-2">Update Status</span>
      </div> */}
      <form onSubmit={handleSubmit(onSubmitForm)} className="flex flex-col gap-4 pt-6">
        <div className="flex justify-between items-center w-full">
          {type === 'unitPricing' ? (
            <FormInput
              width={100}
              label={'Status'}
              register={register}
              register_key={'status'}
              type={'dropdown'}
              defaultValuePre={''}
              options={[
                { value: '', label: 'Select Status', disabled: true },
                ...(Array.isArray(statuses)
                  ? statuses.map((item) => ({
                      value: item?.trim(),
                      label: item?.replace(/_/g, ' ')?.trim() // Replace underscores with spaces for display
                    }))
                  : [])
              ]}
              errors={errors.status}
            />
          ) : (
            <FormInput
              width={100}
              label={'Status'}
              register={register}
              register_key={'status'}
              type={'dropdown'}
              defaultValuePre={''}
              options={[
                { value: '', label: 'Select Status', disabled: true },
                ...(Array.isArray(statuses)
                  ? statuses.map((item) => ({
                      value: item?.name?.trim(),
                      label: item?.name?.replace(/_/g, ' ')?.trim() // Replace underscores with spaces for display
                    }))
                  : [])
              ]}
              errors={errors.status}
            />
          )}
        </div>
        {reservedLabel ? (
          <div className="w-full">
            <label className="mb-1 block text-sm text-gray-700">
              Reserved for ({reservedLabel}) <span className="text-red-500">*</span>
            </label>
            <select
              value={partyRef}
              onChange={(e) => {
                setPartyRef(e.target.value);
                setPartyError('');
              }}
              disabled={isLoadingParties}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-[#346C75] focus:outline-none focus:ring-1 focus:ring-[#346C75]">
              <option value="">
                {isLoadingParties ? 'Loading…' : `Select ${reservedLabel.toLowerCase()}`}
              </option>
              {partyOptions.map((o) => (
                <option key={o.ref} value={o.ref}>
                  {o.name}
                  {o.source === 'partner' ? ' (shareholder)' : o.source === 'builder' ? ' (builder)' : ''}
                </option>
              ))}
              <option value={ADD_NEW}>+ Add new name</option>
            </select>
            {partyRef === ADD_NEW ? (
              <input
                type="text"
                autoFocus
                value={newPartyName}
                onChange={(e) => {
                  setNewPartyName(e.target.value);
                  setPartyError('');
                }}
                placeholder={`${reservedLabel} name`}
                maxLength={255}
                className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:border-[#346C75] focus:outline-none focus:ring-1 focus:ring-[#346C75]"
              />
            ) : null}
            {partyError ? <p className="mt-1 text-xs text-red-600">{partyError}</p> : null}
            <p className="mt-1 text-xs text-gray-500">
              A new name is saved and offered next time. The unit stops showing as vacant.
            </p>
          </div>
        ) : null}
        <div className="w-full flex justify-end items-center space-x-4 mt-8">
          <Button type="button" onClick={onClose} displayType="secondary">
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting || selectedStatus === 'Booked'}>
            Save
          </Button>
        </div>
      </form>

      {/* Select Lead Modal */}
      {isSelectLeadModalOpen && (
        <Modal
          width={50}
          name="Select Lead"
          onClose={() => {
            setIsSelectLeadModalOpen(false);
            reset({ status: '' }); // Reset the status when closing the modal
          }}>
          <SelectLead
            onClose={() => {
              setIsSelectLeadModalOpen(false);
              reset({ status: '' }); // Reset the status when closing the modal
            }}
            onLeadSelect={handleLeadSelect}
            isSubmitting={isSubmitting}
            projectName={projectName}
            projectId={projectId}
          />
        </Modal>
      )}

      {/* Close Deal Modal */}
      {isCloseDealModalOpen && (
        <Modal width={50} name="Close Deal" onClose={() => setIsCloseDealModalOpen(false)}>
          <CloseDeal
            onClose={() => setIsCloseDealModalOpen(false)}
            onSubmit={handleCloseDeal}
            isSubmitting={isSubmitting}
            unitData={unitData}
            selectedLead={selectedLead}
          />
        </Modal>
      )}
    </>
  );
};
